import rcm01 from "@/assets/landscape/rcm01.jpg.asset.json";
import rm21 from "@/assets/landscape/rm21.jpg.asset.json";
import titan from "@/assets/landscape/titan.jpg.asset.json";
import frx from "@/assets/landscape/frx.jpg.asset.json";
import paintPro from "@/assets/landscape/paintmaster-pro.jpg.asset.json";
import paintMini from "@/assets/landscape/paintmaster-mini.jpg.asset.json";
import paintUltra from "@/assets/landscape/paintmaster-ultra.jpg.asset.json";
import paintParking from "@/assets/landscape/paintmaster-parking.jpg.asset.json";
import residentialPair from "@/assets/landscape/fr4000-fv2000.jpg.asset.json";
import fl3000 from "@/assets/landscape/fl3000.jpg.asset.json";
import vela from "@/assets/landscape/vela.jpg.asset.json";
import velaLifestyle from "@/assets/landscape/vela-lifestyle.jpg.asset.json";
import landscapeHero from "@/assets/landscape/landscape-hero.jpg.asset.json";
import sportsField from "@/assets/landscape/sports-field.jpg.asset.json";

export const landscapeImages = {
  hero: landscapeHero.url,
  sportsField: sportsField.url,
  velaLifestyle: velaLifestyle.url,
};

export type LandscapeCategory =
  | "Professional Mowing"
  | "Line Marking"
  | "Residential Mowing";

export interface LandscapeProduct {
  slug: string;
  name: string;
  tagline: string;
  category: LandscapeCategory;
  image: string;
  summary: string;
  bestFor: string[];
  highlights: string[];
  specs: { label: string; value: string }[];
}

export const landscapeProducts: LandscapeProduct[] = [
  {
    slug: "rcm01",
    name: "RCM01 Robotic Cylinder Mower",
    tagline: "Tournament-quality cuts, fully autonomous.",
    category: "Professional Mowing",
    image: rcm01.url,
    summary:
      "A reel-cut robotic cylinder mower built for championship fairways. The floating cutting unit follows turf contours to prevent scalping, while precision rollers deliver clean, high-contrast striping.",
    bestFor: ["Golf fairways", "Championship turf", "Premium sports venues"],
    highlights: [
      "Reel cutting system for clean, scissor-like cuts",
      "Floating cutting unit follows turf contours",
      "Professional-grade adjustable striping",
      "One operator can manage multiple mowers remotely",
    ],
    specs: [
      { label: "Dimensions", value: "1905 × 1077 × 1077 mm (75 × 42.4 × 42.4 in.)" },
      { label: "Weight", value: "364 kg (802.5 lb)" },
      { label: "Max Mowing Area per Charge", value: "30,000 m² (7.4 acres)" },
      { label: "Max Mowing Area in 24 Hours", value: "48,000 m² (12 acres)" },
      { label: "Max Mowing Area in 72 Hours", value: "144,000 m² (35.6 acres)" },
      { label: "Cutting Height", value: "7–44 mm (0.28–1.73 in.)" },
      { label: "Cutting Width", value: "685 mm (27 in.)" },
      { label: "Battery Capacity", value: "8 kWh" },
      { label: "Runtime per Charge", value: "10 h" },
      { label: "Charging Time", value: "5 h" },
      { label: "Max Working Slope", value: "15°" },
      { label: "Sensors", value: "RTK, LiDAR, Vision" },
      { label: "IP Rating", value: "IPX4" },
    ],
  },
  {
    slug: "rm21",
    name: "RM21 Multi-Functional Robotic Mower",
    tagline: "One machine. Mow, mark, collect, pick, brush.",
    category: "Professional Mowing",
    image: rm21.url,
    summary:
      "A multi-functional turf platform that switches between mowing, line marking, grass collecting, ball picking, and dew brushing kits. RTK and LiDAR work together for reliable operation under trees and in weak-signal areas.",
    bestFor: ["Golf roughs", "Driving ranges", "Large sports complexes"],
    highlights: [
      "Quick-swap kits: LM01 line marker, BP01 ball picker, GC01 grass collector, DB01 dew brush",
      "Reliable operation in weak-signal areas with RTK + LiDAR",
      "Multi-zone management from a single task assignment",
      "Intelligent path planning within defined work areas",
    ],
    specs: [
      { label: "Dimensions", value: "1330 × 853 × 980 mm (52.4 × 33.6 × 38.6 in.)" },
      { label: "Weight", value: "170 kg (375 lb)" },
      { label: "Max Mowing Area per Charge", value: "12,000 m² (3.0 acres)" },
      { label: "Max Mowing Area in 24 Hours", value: "Fast charge: 36,000 m² (8.9 acres) / Standard: 31,200 m² (7.7 acres)" },
      { label: "Cutting Height", value: "20–127 mm (0.8–5 in.)" },
      { label: "Cutting Width", value: "635 mm (25 in.)" },
      { label: "Battery Capacity", value: "4 kWh" },
      { label: "Runtime per Charge", value: "5 h" },
      { label: "Charging Time", value: "Standard: 5 h / Fast: 3 h" },
      { label: "Max Working Slope", value: "15°" },
      { label: "Sensors", value: "RTK, LiDAR, Vision" },
      { label: "IP Rating", value: "IPX4" },
    ],
  },
  {
    slug: "titan",
    name: "Titan Robotic Mower",
    tagline: "Elite 2-in-1 turf care without the elite price.",
    category: "Professional Mowing",
    image: titan.url,
    summary:
      "A wide-cut robotic mower with triple-sensor guidance that also accepts a line marker kit — mow and mark full-size fields with a single machine.",
    bestFor: ["Fairways", "Soccer and multi-use fields", "Municipal parks"],
    highlights: [
      "Dual-antenna RTK GNSS, LiDAR, and Vision guidance",
      "730 mm cutting width covering 36,000 m² (9 acres) per day",
      "App-adjustable cutting height, 10–100 mm",
      "With the line marker kit: mark up to 10 standard soccer fields per charge",
    ],
    specs: [
      { label: "Dimensions", value: "1400 × 1050 × 650 mm (55.1 × 41.3 × 25.6 in.)" },
      { label: "Weight", value: "122 kg (287 lb)" },
      { label: "Max Mowing Area per Charge", value: "18,000 m² (4.4 acres)" },
      { label: "Max Mowing Area in 24 Hours", value: "36,000 m² (9 acres)" },
      { label: "Max Mowing Area in 72 Hours", value: "108,000 m² (26.7 acres)" },
      { label: "Cutting Height", value: "10–100 mm (0.4–4 in.)" },
      { label: "Cutting Width", value: "730 mm (28.74 in.)" },
      { label: "Battery Capacity", value: "2 kWh" },
      { label: "Runtime per Charge", value: "8 h" },
      { label: "Charging Time", value: "2 h" },
      { label: "Max Working Slope", value: "25°" },
      { label: "Sensors", value: "RTK, LiDAR, Vision" },
      { label: "IP Rating", value: "IP64" },
    ],
  },
  {
    slug: "frx",
    name: "FRX Robotic Mower",
    tagline: "Professional turf management made affordable.",
    category: "Professional Mowing",
    image: frx.url,
    summary:
      "A compact, slope-capable robotic mower for roughs, embankments, and multi-field sports operations, with fast battery swapping for continuous work.",
    bestFor: ["Steep roughs and embankments", "Sports fields", "Grounds crews"],
    highlights: [
      "Stable mowing on slopes up to 30°",
      "Adjustable cutting height 10–100 mm",
      "Battery swapping for 24/7 mowing plus auto-charging",
      "Multi-field task management",
    ],
    specs: [
      { label: "Dimensions", value: "1278 × 782 × 740 mm (50.3 × 30.8 × 29.1 in.)" },
      { label: "Weight", value: "83.7 kg (184.53 lb)" },
      { label: "Max Mowing Area per Charge", value: "8,000 m² (2 acres)" },
      { label: "Max Mowing Area in 24 Hours", value: "32,000 m² (8 acres)" },
      { label: "Max Mowing Area in 72 Hours", value: "96,000 m² (23.7 acres)" },
      { label: "Cutting Height", value: "10–100 mm (0.4–4 in.)" },
      { label: "Cutting Width", value: "520 mm (20.5 in.)" },
      { label: "Battery Capacity", value: "1 kWh" },
      { label: "Runtime per Charge", value: "5.3 h" },
      { label: "Charging Time", value: "2.5 h" },
      { label: "Max Working Slope", value: "30°" },
      { label: "Sensors", value: "RTK, Vision" },
      { label: "IP Rating", value: "IPX5" },
    ],
  },
  {
    slug: "paintmaster-pro",
    name: "PaintMaster Pro",
    tagline: "Make line marking easier.",
    category: "Line Marking",
    image: paintPro.url,
    summary:
      "Factory-calibrated autonomous line marker with a 20 L tank (expandable to 25 L), real-time obstacle detection, and fleet and multi-field management.",
    bestFor: ["Schools and universities", "Municipal field crews", "Sports complexes"],
    highlights: [
      "Ready to mark on arrival — factory calibrated",
      "20 L tank expandable to 25 L for multi-field marking",
      "1 kWh battery for extended runtime",
      "Studded tires maintain traction on turf",
    ],
    specs: [
      { label: "Dimensions", value: "1074 × 1023 × 672 mm (42.3 × 40.3 × 26.5 in.)" },
      { label: "Weight", value: "73.4 kg (161.8 lb)" },
      { label: "Paint Tank Capacity", value: "20 L (5.3 gal), expandable to 25 L (6.6 gal)" },
      { label: "Marking Speed", value: "3 km/h (1.9 mph)" },
      { label: "Line Width Range", value: "6–17 cm (2.4–6.7 in.)" },
      { label: "Nozzle Height", value: "3–11 cm (1.2–4.3 in.)" },
    ],
  },
  {
    slug: "paintmaster-mini",
    name: "PaintMaster Mini",
    tagline: "Built light. Marks right.",
    category: "Line Marking",
    image: paintMini.url,
    summary:
      "A lightweight autonomous line marker built for easy transport and field-to-field movement, with quick-swap batteries to minimize downtime.",
    bestFor: ["Youth leagues", "Small schools", "Mobile marking crews"],
    highlights: [
      "Lightweight body — simple to transport",
      "8 L paint tank balances efficiency and portability",
      "Quick-swap battery design",
    ],
    specs: [
      { label: "Dimensions", value: "656 × 655 × 453 mm (25.8 × 25.8 × 17.83 in.)" },
      { label: "Weight", value: "21.85 kg (48.2 lb)" },
      { label: "Paint Tank Capacity", value: "8 L (2.1 gal)" },
      { label: "Marking Speed", value: "2 km/h (1.2 mph)" },
      { label: "Line Width Range", value: "5–12 cm (2.0–4.7 in.)" },
      { label: "Nozzle Height", value: "3–8 cm (1.2–3.1 in.)" },
    ],
  },
  {
    slug: "paintmaster-ultra",
    name: "PaintMaster Ultra",
    tagline: "Precision anywhere. Perfection everywhere.",
    category: "Line Marking",
    image: paintUltra.url,
    summary:
      "Millimeter-level line marking for elite venues, using total station plus GNSS positioning to stay accurate near grandstands, buildings, and other challenging structures.",
    bestFor: ["Stadiums", "Professional pitches", "Elite training facilities"],
    highlights: [
      "Total Station + GNSS positioning",
      "Reliable near grandstands and buildings",
      "Built for professional competition standards",
    ],
    specs: [
      { label: "Dimensions", value: "1074 × 1023 × 672 mm (42.3 × 40.3 × 26.5 in.)" },
      { label: "Weight", value: "75 kg (165.3 lb)" },
      { label: "Line Width Range", value: "6–17 cm (2.4–6.7 in.)" },
      { label: "Line Marking Speed", value: "1–3 km/h (0.6–1.9 mph)" },
      { label: "Nozzle Ground Clearance", value: "3–8 cm (1.2–3.1 in.)" },
      { label: "Paint Tank Capacity", value: "25 L (6.6 gal)" },
      { label: "Battery Capacity", value: "1 kWh" },
      { label: "Runtime", value: "8 h" },
      { label: "Charging Time", value: "5 h" },
      { label: "Positioning", value: "Total Station + GNSS" },
      { label: "IP Rating", value: "IPX3" },
    ],
  },
  {
    slug: "paintmaster-parking",
    name: "PaintMaster Parking",
    tagline: "From site capture to professional parking lot line marking.",
    category: "Line Marking",
    image: paintParking.url,
    summary:
      "High-pressure autonomous striping robot for parking lots and hard-surface sites. A fully digital workflow — 3D site scan, layout reconstruction, route simulation, AI vision alignment, then robotic striping — supports both new layouts and repainting of existing lines.",
    bestFor: [
      "Outdoor parking lots",
      "Industrial parks and corporate campuses",
      "Logistics parks and loading zones",
    ],
    highlights: [
      "New layout striping: stalls, arrows, access aisles, no-parking zones, crosswalks",
      "Existing-line repainting with AI vision-based alignment to faded edges and centerlines",
      "Scan-to-stripe digital workflow with route and spray simulation before work starts",
      "Dual-layer safety: 360° real-time monitoring plus physical collision protection",
      "Quick-swap batteries and resume spraying to reduce downtime on large projects",
      "Fleet management across multi-zone parking projects with real-time app monitoring",
    ],
    specs: [
      { label: "Dimensions", value: "1700 × 1120 × 980 mm (66.93 × 44.09 × 38.58 in.)" },
      { label: "Weight", value: "210 kg (463 lb)" },
      { label: "Line Width Range", value: "10–25 cm (3.94–9.84 in.)" },
      { label: "Marking Speed", value: "1–4 km/h" },
      { label: "Paint Tank Capacity", value: "19 L (5 gal)" },
      { label: "Maximum Flow Rate", value: "2 L/min" },
      { label: "Maximum Pressure", value: "20 MPa" },
      { label: "Battery", value: "48 V, 4 kWh — quick-swap supported" },
      { label: "Runtime / Charging", value: "6 h runtime, 4 h charge" },
      { label: "Connectivity", value: "4G and wireless, OTA software updates" },
      { label: "Water Resistance", value: "IPX4" },
      { label: "Operating Temperature", value: "5–35 °C" },
    ],
  },
  {
    slug: "fr4000",
    name: "FR4000 Robotic Mower",
    tagline: "For large estates, courtyards, and municipal green spaces.",
    category: "Residential Mowing",
    image: residentialPair.url,
    summary:
      "Wire-free RTK and vision mowing for large properties, with real-time detection of people, pets, and small animals and 33° climbing ability.",
    bestFor: ["Estates", "HOA common areas", "Municipal green spaces"],
    highlights: [
      "No boundary wires — quick setup",
      "Drop & Run for separated lawn areas",
      "Real-time object and animal detection",
      "Climbs inclines up to 33° (65%)",
    ],
    specs: [
      { label: "Recommended Mowing Area", value: "4,000 m² (1 acre)" },
      { label: "Technology", value: "RTK + RGB Vision (binocular camera)" },
      { label: "Weight", value: "13.3 kg (29 lb)" },
      { label: "Dimensions", value: "645 × 444 × 420 mm (25.4 × 17.5 × 16.5 in.)" },
      { label: "Max Climbing Ability", value: "33° (65%)" },
      { label: "Max Mowing Slope", value: "25° (47%)" },
      { label: "Cutting Height", value: "20–60 mm (0.8–2.4 in.); 10–50 mm (0.4–2.0 in.)" },
      { label: "Cutting Width", value: "260 mm (10.2 in.)" },
      { label: "Battery Capacity", value: "190 Wh" },
      { label: "Runtime per Charge", value: "3 h" },
      { label: "Charging Time", value: "2 h" },
      { label: "IP Rating", value: "Mower: IPX5; Charging Station: IP65" },
    ],
  },
  {
    slug: "fv2000",
    name: "FV2000 Robotic Mower",
    tagline: "For small to medium courtyards.",
    category: "Residential Mowing",
    image: residentialPair.url,
    summary:
      "A compact wire-free robotic mower for residential yards, with smart path planning and real-time obstacle avoidance.",
    bestFor: ["Residential yards", "Small courtyards", "Property managers"],
    highlights: [
      "No boundary wires or complex installation",
      "Smart path planning",
      "Climbs inclines up to 33° (65%)",
    ],
    specs: [
      { label: "Recommended Mowing Area", value: "2,000 m² (0.5 acre)" },
      { label: "Technology", value: "RTK + RGB Vision (monocular camera)" },
      { label: "Weight", value: "13.2 kg (29 lb)" },
      { label: "Dimensions", value: "634 × 444 × 285 mm (24.9 × 17.5 × 11.2 in.)" },
      { label: "Max Climbing Ability", value: "33° (65%)" },
      { label: "Max Mowing Slope", value: "25° (47%)" },
      { label: "Cutting Height", value: "20–60 mm (0.8–2.4 in.)" },
      { label: "Cutting Width", value: "260 mm (10.2 in.)" },
      { label: "Battery Capacity", value: "95 Wh" },
      { label: "Runtime per Charge", value: "2 h" },
      { label: "Charging Time", value: "1 h" },
      { label: "IP Rating", value: "Mower: IPX5; Charging Station: IP65" },
    ],
  },
  {
    slug: "fl3000",
    name: "FL3000 LiDAR Robotic Mower",
    tagline: "For yards with dense trees, walls, and weak GPS signals.",
    category: "Residential Mowing",
    image: fl3000.url,
    summary:
      "LiDAR-powered mowing with no wires and no RTK required — define virtual boundaries in the app and let it navigate tight corners and shaded areas.",
    bestFor: ["Tree-dense yards", "Walled gardens", "Weak-signal properties"],
    highlights: [
      "No wire, no RTK, 10-minute setup",
      "Reliable in weak-signal areas",
      "Smart edge trimming with LiDAR + vision",
    ],
    specs: [
      { label: "Recommended Mowing Area", value: "3,000 m² (0.75 acre)" },
      { label: "Technology", value: "LiDAR + RGB Vision" },
      { label: "Weight", value: "13.5 kg (30 lb)" },
      { label: "Dimensions", value: "633 × 443 × 302 mm (24.9 × 17.4 × 11.8 in.)" },
      { label: "Max Climbing Ability", value: "33° (65%)" },
      { label: "Max Mowing Slope", value: "25° (47%)" },
      { label: "Cutting Height", value: "20–60 mm (0.8–2.4 in.); 10–50 mm (0.4–2.0 in.)" },
      { label: "Cutting Width", value: "260 mm (10.2 in.)" },
      { label: "Battery Capacity", value: "190 Wh" },
      { label: "Runtime per Charge", value: "3 h" },
      { label: "Charging Time", value: "2 h" },
      { label: "IP Rating", value: "Mower: IPX5; Charging Station: IP65" },
    ],
  },
  {
    slug: "vela",
    name: "Vela Robotic Mower",
    tagline: "Compact autonomy for demanding yards.",
    category: "Residential Mowing",
    image: vela.url,
    summary:
      "A compact autonomous mower engineered for steep, complex residential lawns with a long runtime per charge.",
    bestFor: ["Sloped lawns", "Residential estates", "Landscape service routes"],
    highlights: [
      "Climbs inclines up to 33°",
      "Up to 4 hours of runtime per charge",
      "Lightweight 13.5 kg body for easy transport between properties",
    ],
    specs: [
      { label: "Dimensions", value: "632 × 440 × 412 mm" },
      { label: "Weight", value: "13.5 kg" },
      { label: "Runtime per Charge", value: "4 h" },
      { label: "Max Climbing Ability", value: "33°" },
    ],
  },
];

export const landscapeKits = [
  {
    name: "LM01 Line Marker Kit (for RM21)",
    specs: [
      "68 kg (150 lb) · 684 × 562 × 432 mm",
      "25 L (5.5 gal) paint tank",
      "Line width 50–170 mm (2–6.7 in.)",
      "Up to 20 h marking per charge · up to 20 full-size fields",
    ],
  },
  {
    name: "BP01 Ball Picker Kit (for RM21)",
    specs: [
      "70 kg (154 lb) · 1480 × 1120 × 590 mm",
      "900 mm (35.4 in.) picking width",
      "2 baskets · 400 golf balls per basket",
      "Full-basket detection and auto-return",
    ],
  },
  {
    name: "GC01 Grass Collector Kit (for RM21)",
    specs: [
      "100 kg (220 lb) · 1260 × 960 × 740 mm",
      "800 mm (31.5 in.) collection width",
      "150 L (33 gal) max collection volume",
      "Collection height 20–50 mm · IPX4",
    ],
  },
  {
    name: "DB01 Dew Brush Kit (for RM21)",
    specs: [
      "28 kg (61.7 lb)",
      "Single section 1000 mm · three sections 2930 mm",
      "Automatic lifting and folding",
      "Customizable brushing direction",
    ],
  },
];

export const getLandscapeProduct = (slug?: string) =>
  landscapeProducts.find((p) => p.slug === slug);
